import { DownloadOutlined } from '@ant-design/icons'
import { Button, Card, Space, Typography } from 'antd'
import React, { useState } from 'react'
import ErrorMessage from 'utils/ErrorMessage'
import SuccessMessage from 'utils/SuccessMessage'
import WarningMessage from 'utils/WarningMessage'
import { exportAuditLogs } from '../api'
import useAuditLogs from '../hooks/useAuditLogs'
import AuditFilter from '../Partials/AuditFilter'
import AuditTable from '../Partials/AuditTable'

const { Title, Paragraph } = Typography

/**
 * Audit log screen — who did what, across every portal.
 *
 * Business logic lives in useAuditLogs; this component wires it to the filter
 * bar, the table and the export button.
 */
const AuditLogContainer = () => {
  const {
    rows,
    actions,
    entityTypes,
    filters,
    isFiltered,
    isLoading,
    isLoadingMore,
    hasMore,
    applyFilters,
    resetFilters,
    loadMore,
  } = useAuditLogs()

  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const { blob, filename, isTruncated, rowCount } = await exportAuditLogs(filters)

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      if (isTruncated) {
        // Never let a capped export look like the whole trail.
        WarningMessage(
          `Export capped at ${rowCount} entries. Narrow the date range to export the rest.`
        )
      } else {
        SuccessMessage(`Exported ${rowCount} entries`)
      }
    } catch (error) {
      ErrorMessage(error?.response?.data?.message || 'Unable to export the audit log')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Card>
      <Space
        align="start"
        style={{ width: '100%', justifyContent: 'space-between' }}
      >
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            Audit log
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Every privileged action across the admin, corporate and institute
            portals. Entries cannot be edited or deleted.
          </Paragraph>
        </div>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
          loading={isExporting}
          disabled={isLoading}
        >
          Export
        </Button>
      </Space>

      <AuditFilter
        actions={actions}
        entityTypes={entityTypes}
        isLoading={isLoading}
        onApply={applyFilters}
        onReset={resetFilters}
      />

      <AuditTable
        rows={rows}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        isFiltered={isFiltered}
        onLoadMore={loadMore}
      />
    </Card>
  )
}

export default AuditLogContainer
